import { Section, useToast } from '@components/index'
import { SectionTitle } from '@components/section/SectionTitle'
import { EditRentFormModal } from '@features/contracts/components/RentEdit'
import RentPayment from '@features/contracts/components/RentPayment'
import { BulkMarkPaidModal } from '@features/dashboard/components/BulkMarkPaidModal'
import { useSepaExport } from '@services/dashboard/mutations'
import type { UnpaidRents } from '@services/dashboard/queries/useUnpaidRents'
import { format } from 'date-fns'
import dayjs from 'dayjs'
import {
  Check,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Download,
  Eye
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { UnpaidRent } from 'types/api'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

interface UnpaidRentsTablesProps {
  rents?: UnpaidRents
  loading?: boolean
}

const TYPE_TONE: Record<string, string> = {
  RENT: 'bg-info-soft text-info',
  DEPOSIT: 'bg-success-soft text-success',
  LANDLORD_RENT: 'bg-peach/30 text-warning',
  LANDLORD_DEPOSIT: 'bg-peach/30 text-warning'
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' }).format(
    amount
  )

const TH =
  'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground'
const TD = 'px-4 py-3.5 align-middle text-sm text-ink'
const PAGE_SIZE = 10

function UnpaidTable({
  data,
  onRowClick,
  onView,
  selectable,
  selectedIds,
  onToggleRow,
  onToggleAll
}: {
  data: UnpaidRent[]
  onRowClick: (rent: UnpaidRent) => void
  onView: (rent: UnpaidRent) => void
  selectable?: boolean
  selectedIds?: Set<string>
  onToggleRow?: (id: string) => void
  onToggleAll?: (ids: string[], checked: boolean) => void
}) {
  const { t } = useTranslation('dashboard')
  const [page, setPage] = useState(0)

  const typeLabel = (type: string) =>
    t(
      `rentTable.paymentTypes.${
        {
          RENT: 'rent',
          DEPOSIT: 'deposit',
          LANDLORD_RENT: 'landlordRent',
          LANDLORD_DEPOSIT: 'landlordDeposit'
        }[type] ?? 'rent'
      }`,
      { defaultValue: type }
    )

  if (!data.length) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">—</p>
    )
  }

  const pages = Math.ceil(data.length / PAGE_SIZE)
  const rows = data.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)

  // Select-all covers the visible page only. Selecting rows the admin can't
  // see is how a single click ends up marking payments nobody reviewed.
  const pageIds = rows.map((r) => r.id).filter(Boolean) as string[]
  const allChecked =
    pageIds.length > 0 && pageIds.every((id) => selectedIds?.has(id))

  return (
    <div>
      <div className="overflow-x-auto rounded-2xl border border-ink/[0.06]">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-ink/[0.08] bg-ink/[0.02] text-left">
              {selectable && (
                <th className={cn(TH, 'w-12')}>
                  <Checkbox
                    checked={allChecked}
                    onCheckedChange={(c) => onToggleAll?.(pageIds, !!c)}
                    aria-label="select all"
                  />
                </th>
              )}
              <th className={TH}>{t('rentTable.user')}</th>
              <th className={TH}>{t('rentTable.paymentType')}</th>
              <th className={cn(TH, 'text-right')}>{t('rentTable.amount')}</th>
              <th className={TH}>{t('rentTable.dueDate')}</th>
              <th className={TH}>{t('rentTable.variableSymbol')}</th>
              <th className={TH}>{t('rentTable.specificSymbol')}</th>
              <th className={cn(TH, 'text-center')}>{t('rentTable.action')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((rent) => (
              <tr
                key={rent.id}
                onClick={() => onRowClick(rent)}
                className="cursor-pointer border-b border-ink/[0.05] transition-colors last:border-0 hover:bg-ink/[0.015]"
              >
                {selectable && (
                  <td
                    className={TD}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={!!rent.id && selectedIds?.has(rent.id)}
                      onCheckedChange={() => rent.id && onToggleRow?.(rent.id)}
                      aria-label="select row"
                    />
                  </td>
                )}
                <td className={TD}>
                  <div className="font-medium text-ink">
                    {rent.user_name ?? rent.user_email}
                  </div>
                  {rent.user_name && (
                    <div className="text-xs text-muted-foreground">
                      {rent.user_email}
                    </div>
                  )}
                </td>
                <td className={TD}>
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                      TYPE_TONE[rent.payment_type] ?? 'bg-ink/[0.07] text-ink/60'
                    )}
                  >
                    {typeLabel(rent.payment_type)}
                  </span>
                </td>
                <td className={cn(TD, 'whitespace-nowrap text-right font-semibold')}>
                  {formatCurrency(rent.amount)}
                </td>
                <td className={cn(TD, 'whitespace-nowrap')}>
                  {format(new Date(rent.due_date), 'dd.MM.yyyy')}
                </td>
                <td className={TD}>{rent.variable_symbol}</td>
                <td className={TD}>{rent.specific_symbol || '-'}</td>
                <td className={cn(TD, 'text-center')}>
                  <Button
                    variant="ghostDark"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      onView(rent)
                    }}
                  >
                    <Eye className="h-4 w-4" />
                    {t('rentTable.view')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-end gap-1.5 py-3">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink transition-colors hover:bg-ink/[0.05] disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="px-2 text-sm font-medium text-muted-foreground">
            {page + 1} / {pages}
          </span>
          <button
            type="button"
            disabled={page === pages - 1}
            onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink transition-colors hover:bg-ink/[0.05] disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}

export function UnpaidRentsTables({
  rents,
  loading = false
}: UnpaidRentsTablesProps) {
  const { t } = useTranslation('dashboard')
  const [tab, setTab] = useState<'userRents' | 'landlordPayments'>('userRents')
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedRent, setSelectedRent] = useState<UnpaidRent | null>(null)
  const [sepaMonth, setSepaMonth] = useState(() =>
    dayjs().add(1, 'month').format('YYYY-MM')
  )
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkOpen, setBulkOpen] = useState(false)
  const navigate = useNavigate()
  const sepaExport = useSepaExport()
  const toast = useToast()

  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const toggleAll = (ids: string[], checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)))
      return next
    })

  const switchTab = (next: 'userRents' | 'landlordPayments') => {
    setSelected(new Set())
    setTab(next)
  }

  const handleSepaExport = () => {
    const m = dayjs(sepaMonth)
    sepaExport.mutate(
      { year: m.year(), month: m.month() + 1 },
      {
        onSuccess: () => toast.success({ message: t('sepaExport.success') }),
        onError: () => toast.error({ message: t('sepaExport.error') })
      }
    )
  }

  const handleView = (rent: UnpaidRent) => {
    setSelectedRent(rent)
    setIsModalVisible(true)
  }
  const handleCloseModal = () => {
    setIsModalVisible(false)
    setSelectedRent(null)
  }

  const tabs = [
    { key: 'userRents' as const, label: t('rentTable.unpaidUserRents') },
    {
      key: 'landlordPayments' as const,
      label: t('rentTable.unpaidLandlordPayments')
    }
  ]

  const rentToPayment = (rent: UnpaidRent) => ({
    id: rent.id,
    due_date: rent.due_date,
    amount: rent.amount.toString(),
    payment_type: rent.payment_type as never,
    variable_symbol: rent.variable_symbol,
    paid: false,
    pay_by_square: rent.pay_by_square,
    specific_symbol: rent.specific_symbol,
    description: rent.description,
    bank_account: rent.bank_account ?? undefined
  })

  return (
    <Section
      defaultActiveKey={['0']}
      title={
        <SectionTitle
          title={t('upcomingUnpaidRents')}
          icon={<CreditCard />}
        />
      }
    >
      <div className="space-y-4">
        <div className="inline-flex flex-wrap gap-1 rounded-full border border-ink/[0.06] bg-ink/[0.03] p-1">
          {tabs.map((tabItem) => (
            <button
              key={tabItem.key}
              type="button"
              onClick={() => switchTab(tabItem.key)}
              className={cn(
                'rounded-full border-0 px-4 py-2 text-sm font-semibold transition-colors',
                tab === tabItem.key
                  ? 'bg-white text-ink shadow-sm'
                  : 'bg-transparent text-muted-foreground hover:text-ink'
              )}
            >
              {tabItem.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="h-24 animate-pulse rounded-xl bg-ink/[0.04]" />
        ) : tab === 'userRents' ? (
          <UnpaidTable
            data={rents?.unpaid_user_rents ?? []}
            onRowClick={(rent) => navigate(`/admin/offers/${rent.offer}`)}
            onView={handleView}
          />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {selected.size > 0 ? (
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-ink">
                    {t('rentTable.selected', { count: selected.size })}
                  </span>
                  <Button size="sm" onClick={() => setBulkOpen(true)}>
                    <Check className="h-4 w-4" />
                    {t('rentTable.markPaid')}
                  </Button>
                </div>
              ) : (
                <span />
              )}
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="text-sm text-muted-foreground">
                  {t('sepaExport.selectMonth')}:
                </span>
                <Input
                  type="month"
                  value={sepaMonth}
                  onChange={(e) => e.target.value && setSepaMonth(e.target.value)}
                  className="w-44"
                />
                <Button
                  variant="ghostDark"
                  onClick={handleSepaExport}
                  disabled={sepaExport.isPending}
                >
                  <Download className="h-4 w-4" />
                  {t('sepaExport.button')}
                </Button>
              </div>
            </div>
            <UnpaidTable
              data={rents?.unpaid_landlord_payments ?? []}
              onRowClick={(rent) => navigate(`/admin/flats/${rent.flat}`)}
              onView={handleView}
              selectable
              selectedIds={selected}
              onToggleRow={toggleRow}
              onToggleAll={toggleAll}
            />
          </div>
        )}
      </div>

      <BulkMarkPaidModal
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        rentIds={Array.from(selected)}
        onSettled={(failedIds) => setSelected(new Set(failedIds))}
      />

      <Dialog open={isModalVisible} onOpenChange={(o) => !o && handleCloseModal()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {selectedRent?.payment_type === 'RENT' ||
              selectedRent?.payment_type === 'LANDLORD_RENT'
                ? t('rentTable.rentDetails')
                : t('rentTable.depositDetails')}
            </DialogTitle>
          </DialogHeader>
          {selectedRent && (
            <div className="space-y-4">
              <RentPayment rent={rentToPayment(selectedRent)} embedded />
              {selectedRent.id && (
                <div className="flex justify-end">
                  <EditRentFormModal
                    rentId={selectedRent.id}
                    initialValues={rentToPayment(selectedRent)}
                    onSuccess={handleCloseModal}
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Section>
  )
}
